import { loadSession } from '../../auth/state.js'

export const COMMERCE_SECTIONS = [
  { id: 'orders', label: '订单', labelEn: 'Orders', endpoint: 'orders', responseKey: 'orders' },
  { id: 'payments', label: '支付', labelEn: 'Payments', endpoint: 'payments', responseKey: 'orders' },
  { id: 'products', label: '商品', labelEn: 'Products', endpoint: 'products', responseKey: 'products' },
  { id: 'fulfillment', label: '履约', labelEn: 'Fulfillment', endpoint: 'fulfillment', responseKey: 'orders' },
  { id: 'tracking', label: '物流追踪', labelEn: 'Tracking', endpoint: 'tracking', responseKey: 'orders' },
  { id: 'exceptions', label: '异常', labelEn: 'Exceptions', endpoint: 'exceptions', responseKey: 'auditLogs' },
]

export const COMMERCE_DATA_RESOURCES = Object.freeze({
  overview: 'overview',
  health: 'health',
  'audit-logs': 'audit-logs',
  'provider-configs': 'provider-configs',
  'notification-channels': 'notification-channels',
  'notification-deliveries': 'notification-deliveries',
})

const SETTLED_ORDER_STATUSES = new Set(['paid', 'partially_refunded', 'refunded', 'fulfillment', 'completed'])
const FAILED_PAYMENT_STATUSES = new Set(['failed', 'requires_action', 'requires_payment_method', 'processing', 'pending', 'incomplete', 'canceled', 'cancelled'])
const FULFILLMENT_ELIGIBLE_ORDER_STATUSES = new Set(['paid', 'fulfillment', 'partially_refunded'])

export function hasCommerceSuccessfulPayment(record) {
  const order = record?.order && typeof record.order === 'object' ? record.order : record
  const paymentStatus = String(order?.paymentStatus || '').trim().toLowerCase()
  const orderStatus = String(order?.status || '').trim().toLowerCase()
  const paidAmount = Number(order?.amounts?.paid)
  if (FAILED_PAYMENT_STATUSES.has(paymentStatus)) return false
  if ((paymentStatus === 'succeeded' || paymentStatus === 'paid') && Number.isFinite(paidAmount) && paidAmount > 0) return true
  // A settled order is useful legacy evidence only when payment status is absent.
  // Never infer success from a positive amount alone: cancelled data can retain it.
  return !paymentStatus
    && SETTLED_ORDER_STATUSES.has(orderStatus)
    && Number.isFinite(paidAmount)
    && paidAmount > 0
}

export function hasCommerceFulfillmentActivity(record) {
  const order = record?.order && typeof record.order === 'object' ? record.order : record
  const status = String(order?.status || '').trim().toLowerCase()
  return Boolean(order?.shipmentId || order?.fulfillmentStatus)
    || FULFILLMENT_ELIGIBLE_ORDER_STATUSES.has(status)
}

export function isCommerceLogisticsAuditEvent(record) {
  return /^(fulfillment|tracking|shipment|package)\./i.test(String(record?.action || record?.type || '').trim())
}

export function hasCommerceTrackingActivity(record) {
  const order = record?.order && typeof record.order === 'object' ? record.order : record
  return Boolean(order?.trackingNumber || order?.trackingStatus || order?.packageId || order?.trackingException)
}

export async function fetchCommerceData(resource, { fetchImpl = fetch, token, signal } = {}) {
  const endpoint = COMMERCE_DATA_RESOURCES[resource]
  if (!endpoint) throw new Error('Unknown commerce data resource')
  const accessToken = token ?? loadSession()?.tokens?.accessToken
  if (!accessToken) {
    const error = new Error('Missing access token')
    error.status = 401
    throw error
  }

  const response = await fetchImpl(`/commerce-admin-api/${endpoint}`, {
    cache: 'no-store',
    signal,
    headers: { Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok || body?.ok === false || (typeof body?.code === 'number' && body.code !== 0)) {
    const error = new Error(body?.message || body?.error || body?.msg || `HTTP ${response.status}`)
    error.status = response.status
    throw error
  }
  return body?.data ?? body
}

export async function fetchCommerceResourcePage(section, { fetchImpl = fetch, token, signal, filters = {} } = {}) {
  const resource = COMMERCE_SECTIONS.find((item) => item.id === section)
  if (!resource) throw new Error('Unknown commerce resource')
  const accessToken = token ?? loadSession()?.tokens?.accessToken
  if (!accessToken) {
    const error = new Error('Missing access token')
    error.status = 401
    throw error
  }

  const allowedFilters = new Set(['cursor', 'limit', 'query', 'status', 'paymentStatus', 'paymentProvider', 'fulfillmentStatus', 'trackingStatus', 'createdFrom', 'createdTo'])
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (allowedFilters.has(key) && value != null && value !== '') query.set(key, String(value))
  }
  const suffix = query.size ? `?${query}` : ''
  const response = await fetchImpl(`/commerce-admin-api/${resource.endpoint}${suffix}`, {
    cache: 'no-store',
    signal,
    headers: { Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok || body?.ok === false || (typeof body?.code === 'number' && body.code !== 0)) {
    const error = new Error(body?.message || body?.error || body?.msg || `HTTP ${response.status}`)
    error.status = response.status
    throw error
  }
  const payload = body?.data ?? body
  const records = Array.isArray(payload) ? payload : payload?.items ?? payload?.[resource.responseKey || resource.endpoint]
  if (!Array.isArray(records)) throw new Error('Invalid Commerce list response')
  return { records, nextCursor: typeof payload?.nextCursor === 'string' ? payload.nextCursor : null }
}

export async function fetchCommerceResource(section, options = {}) {
  return (await fetchCommerceResourcePage(section, options)).records
}


export async function fetchCommerceOrder(orderId, { fetchImpl = fetch, token, signal } = {}) {
  const normalizedId = String(orderId || '').trim()
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(normalizedId)) throw new Error('Invalid order ID')
  const accessToken = token ?? loadSession()?.tokens?.accessToken
  if (!accessToken) {
    const error = new Error('Missing access token')
    error.status = 401
    throw error
  }

  const response = await fetchImpl(`/commerce-admin-api/orders/${encodeURIComponent(normalizedId)}`, {
    cache: 'no-store',
    signal,
    headers: { Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok || body?.ok === false || (typeof body?.code === 'number' && body.code !== 0)) {
    const error = new Error(body?.message || body?.error || body?.msg || `HTTP ${response.status}`)
    error.status = response.status
    throw error
  }
  return body?.data ?? body
}
